import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart3, TrendingUp, ShoppingCart, Calendar as CalendarIcon, DollarSign, Package, ListFilter } from "lucide-react";
import { LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const DEFAULT_COLORS = ['#0F4C75', '#3282B8', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

// Decide whether to render a dark border for very light slices.
const isLightColor = (hex) => {
  if (!hex) return true;
  const c = hex.replace('#', '');
  if (c.length !== 6) return false;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  // Relative luminance (Rec. 601)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.85;
};

// Truncate long product names for tight chart legends.
const truncateName = (s, n = 22) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s);

// Preset time ranges
const PRESETS = [
  // Past
  { id: "past7", label: "Past 7 Days", desc: "Last 7 days", category: "past" },
  { id: "past14", label: "Past 14 Days", desc: "Last 14 days", category: "past" },
  { id: "past30", label: "Past 30 Days", desc: "Last 30 days", category: "past" },
  // Future
  { id: "next7", label: "Next 7 Days", desc: "Future 7 days", category: "future" },
  { id: "next14", label: "Next 14 Days", desc: "Future 14 days", category: "future" },
  { id: "next30", label: "Next 30 Days", desc: "Future 30 days", category: "future" },
  // Both
  { id: "both7", label: "±7 Days", desc: "7 days around today", category: "both" },
  { id: "both14", label: "±14 Days", desc: "14 days around today", category: "both" },
  { id: "both30", label: "±30 Days", desc: "30 days around today", category: "both" },
];

export default function DashboardTab({ products: productsProp, onNavigateToOrders }) {
  const [rangeType, setRangeType] = useState("both7"); // Preset ID
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [isCustom, setIsCustom] = useState(false);
  const [showTodayOrders, setShowTodayOrders] = useState(false);
  const [hoveredLine, setHoveredLine] = useState(null); // For line chart legend hover
  const [productFilter, setProductFilter] = useState(null); // null = all products; else Set of names
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState(productsProp || []);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    dispatched: 0,
    invoiced: 0,
    canceled: 0
  });

  // Canceled orders should be excluded from revenue / product / trend charts
  // and from "Today's orders" — they represent business that never happened.
  // Keep the raw `orders` for the status breakdown card (which intentionally
  // shows canceled counts) and for the calendar status filter.
  const activeOrders = useMemo(
    () => orders.filter((o) => o.status !== "canceled"),
    [orders],
  );

  useEffect(() => {
    fetchData();
  }, [rangeType, customRange]);

  useEffect(() => {
    if (productsProp) {
      setProducts(productsProp);
    }
  }, [productsProp]);

  // Get product color by name
  const getProductColor = (productName) => {
    const product = products.find(p => p.name === productName);
    return product?.color || DEFAULT_COLORS[products.findIndex(p => p.name === productName) % DEFAULT_COLORS.length] || DEFAULT_COLORS[0];
  };

  // Calculate date range based on selected preset or custom range
  const getDateRange = () => {
    // If custom range is set, use it
    if (isCustom && customRange.from && customRange.to) {
      const startDate = new Date(customRange.from);
      const endDate = new Date(customRange.to);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      return { startDate, endDate };
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    let startDate = new Date(now);
    let endDate = new Date(now);

    switch (rangeType) {
      // 过去
      case "past7":
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "past14":
        startDate.setDate(startDate.getDate() - 14);
        break;
      case "past30":
        startDate.setDate(startDate.getDate() - 30);
        break;
      // 未来
      case "next7":
        endDate.setDate(endDate.getDate() + 7);
        break;
      case "next14":
        endDate.setDate(endDate.getDate() + 14);
        break;
      case "next30":
        endDate.setDate(endDate.getDate() + 30);
        break;
      // 正负
      case "both7":
        startDate.setDate(startDate.getDate() - 7);
        endDate.setDate(endDate.getDate() + 7);
        break;
      case "both14":
        startDate.setDate(startDate.getDate() - 14);
        endDate.setDate(endDate.getDate() + 14);
        break;
      case "both30":
        startDate.setDate(startDate.getDate() - 30);
        endDate.setDate(endDate.getDate() + 30);
        break;
      default:
        startDate.setDate(startDate.getDate() - 7);
        endDate.setDate(endDate.getDate() + 7);
    }

    return { startDate, endDate };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Products come from the parent (AdminDashboard) — no need to refetch.
      const ordersRes = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      
      // Extract all orders from grouped structure
      let allOrders = [];
      if (ordersRes.data.groups) {
        Object.values(ordersRes.data.groups).forEach(group => {
          if (Array.isArray(group)) {
            allOrders = allOrders.concat(group);
          }
        });
      } else if (Array.isArray(ordersRes.data)) {
        allOrders = ordersRes.data;
      }
      
      setOrders(allOrders);
      calculateStats(allOrders);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersData) => {
    const { startDate, endDate } = getDateRange();

    const filteredOrders = ordersData.filter(order => {
      const orderDate = new Date(order.delivery_date);
      return orderDate >= startDate && orderDate <= endDate;
    });

    // `total` represents real business volume — canceled orders are tracked
    // separately in their own card.
    setStats({
      total: filteredOrders.filter(o => o.status !== 'canceled').length,
      pending: filteredOrders.filter(o => o.status === 'pending').length,
      approved: filteredOrders.filter(o => o.status === 'approved').length,
      dispatched: filteredOrders.filter(o => o.status === 'dispatched').length,
      invoiced: filteredOrders.filter(o => o.status === 'invoiced').length,
      canceled: filteredOrders.filter(o => o.status === 'canceled').length,
    });
  };

  // Get date range text
  const getDateRangeText = () => {
    const { startDate, endDate } = getDateRange();
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Handle custom date range selection
  const handleCustomRangeSelect = (range) => {
    if (range?.from) {
      setCustomRange(range);
      // Only activate custom mode when both dates are selected
      if (range.to) {
        setIsCustom(true);
        setShowTodayOrders(false);
      }
    }
  };

  // Handle preset selection
  const handlePresetSelect = (presetId) => {
    setRangeType(presetId);
    setIsCustom(false);
    setShowTodayOrders(false);
    // Clear custom range when switching to preset
    setCustomRange({ from: null, to: null });
  };

  // Handle today's orders view
  const handleTodayOrdersClick = () => {
    setShowTodayOrders(true);
    setIsCustom(false);
  };

  // Get today's orders grouped by product
  const getTodayOrders = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    const todayOrders = activeOrders.filter(order => order.delivery_date === todayStr);
    const productQuantities = {};
    
    todayOrders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          if (item.product_name) {
            productQuantities[item.product_name] = (productQuantities[item.product_name] || 0) + item.quantity;
          }
        });
      }
    });
    
    return Object.entries(productQuantities)
      .map(([name, quantity]) => ({ name, quantity }))
      .sort((a, b) => b.quantity - a.quantity);
  };

  // Revenue trend by date
  const getRevenueTrend = (range) => {
    const { startDate, endDate } = range || getDateRange();
    const dateMap = {};

    // Initialize all dates in range
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      dateMap[dateStr] = { date: dateStr, revenue: 0 };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Calculate revenue by date (active orders only — canceled excluded)
    activeOrders.forEach(order => {
      const dateStr = order.delivery_date;
      if (dateMap[dateStr]) {
        dateMap[dateStr].revenue += order.total_price || 0;
      }
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Product quantity trend
  const getProductTrend = (range) => {
    const { startDate, endDate } = range || getDateRange();
    const dateMap = {};
    const productNames = {};

    // Get top 5 products by quantity in date range (active orders only)
    const productQuantities = {};
    activeOrders.forEach(order => {
      const orderDate = new Date(order.delivery_date);
      if (orderDate >= startDate && orderDate <= endDate && order.items) {
        order.items.forEach(item => {
          productQuantities[item.product_id] = (productQuantities[item.product_id] || 0) + item.quantity;
          productNames[item.product_id] = item.product_name;
        });
      }
    });

    const topProducts = Object.entries(productQuantities)
      .sort(([, a], [, b]) => b - a)
      .map(([id]) => id);

    // Initialize dates
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      dateMap[dateStr] = { date: dateStr };
      topProducts.forEach(productId => {
        dateMap[dateStr][productNames[productId]] = 0;
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Count products by date (active orders only)
    activeOrders.forEach(order => {
      const dateStr = order.delivery_date;
      if (dateMap[dateStr] && order.items) {
        order.items.forEach(item => {
          if (topProducts.includes(item.product_id)) {
            dateMap[dateStr][item.product_name] = (dateMap[dateStr][item.product_name] || 0) + item.quantity;
          }
        });
      }
    });

    return {
      data: Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)),
      productNames: topProducts.map(id => productNames[id])
    };
  };

  // Product revenue distribution (pie chart)
  const getProductRevenue = () => {
    const productRevenue = {};
    const { startDate, endDate } = getDateRange();
    
    // Filter orders in time range by delivery_date (active orders only —
    // canceled don't generate revenue)
    const filteredOrders = activeOrders.filter(order => {
      const orderDate = new Date(order.delivery_date);
      return orderDate >= startDate && orderDate <= endDate;
    });
    
    console.log('Filtered orders for revenue:', filteredOrders.length);
    
    filteredOrders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          // Calculate revenue from order total_price proportionally by item quantity
          const itemRevenue = order.total_price 
            ? (item.quantity / order.items.reduce((sum, i) => sum + i.quantity, 0)) * order.total_price
            : (item.quantity || 0) * (item.price || 0);
          
          if (item.product_name && itemRevenue > 0) {
            productRevenue[item.product_name] = (productRevenue[item.product_name] || 0) + itemRevenue;
          }
        });
      }
    });

    const result = Object.entries(productRevenue)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    console.log('Product Revenue:', result);
    return result;
  };

  // Product quantity distribution (pie chart)
  const getProductQuantity = () => {
    const productQuantity = {};
    const { startDate, endDate } = getDateRange();
    
    // Filter orders in time range (active orders only — canceled excluded)
    const filteredOrders = activeOrders.filter(order => {
      const orderDate = new Date(order.delivery_date);
      return orderDate >= startDate && orderDate <= endDate;
    });
    
    filteredOrders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          if (item.product_name && item.quantity > 0) {
            productQuantity[item.product_name] = (productQuantity[item.product_name] || 0) + item.quantity;
          }
        });
      }
    });

    const result = Object.entries(productQuantity)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    console.log('Product Quantity:', result); // Debug
    return result;
  };

  const revenueTrendData = getRevenueTrend();
  const productTrend = getProductTrend();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  const visibleProducts = productTrend.productNames.filter(
    (n) => !productFilter || productFilter.has(n)
  );
  const toggleProduct = (name) => {
    setProductFilter((prev) => {
      const next = new Set(prev || productTrend.productNames);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next.size === productTrend.productNames.length ? null : next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">Order statistics and trends.</p>
      </div>

      {/* Time Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl text-brand-navy flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            Time Range Selection
          </CardTitle>
          <CardDescription className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
            {!showTodayOrders && (
              <>Current range: <span className="font-semibold text-brand-navy normal-case tracking-normal">{getDateRangeText()}</span></>
            )}
            {showTodayOrders && (
              <>Showing: <span className="font-semibold text-brand-navy normal-case tracking-normal">Today&apos;s Orders by Product</span></>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Actions Row */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant={showTodayOrders ? "default" : "outline"}
              className={`h-auto py-3 flex flex-col items-center ${showTodayOrders ? "bg-brand-navy hover:bg-brand-navy-hover" : ""}`}
              onClick={handleTodayOrdersClick}
            >
              <Package className="h-5 w-5 mb-1" />
              <span className="font-semibold">Today&apos;s Orders</span>
              <span className="text-[10px] uppercase tracking-wider opacity-75 mt-0.5">View by product</span>
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={isCustom ? "default" : "outline"}
                  className={`h-auto py-3 flex flex-col items-center ${isCustom ? "bg-brand-navy hover:bg-brand-navy-hover" : ""}`}
                >
                  <CalendarIcon className="h-5 w-5 mb-1" />
                  <span className="font-semibold">Custom Range</span>
                  <span className="text-[10px] uppercase tracking-wider opacity-75 mt-0.5">
                    {customRange.from && customRange.to
                      ? `${format(customRange.from, "MM/dd")} – ${format(customRange.to, "MM/dd")}`
                      : "Select dates"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={customRange}
                  onSelect={handleCustomRangeSelect}
                  numberOfMonths={2}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Preset Ranges - Compact Layout */}
          <div className="space-y-3">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {PRESETS.map((preset) => {
                const active = !isCustom && !showTodayOrders && rangeType === preset.id;
                return (
                  <Button
                    key={preset.id}
                    variant={active ? "default" : "outline"}
                    className={`h-auto py-2 px-2 text-[11px] uppercase tracking-wider font-semibold ${
                      active ? "bg-brand-navy hover:bg-brand-navy-hover" : ""
                    }`}
                    onClick={() => handlePresetSelect(preset.id)}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Today's Orders Detail */}
      {showTodayOrders && getTodayOrders().length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl text-brand-navy">Today&apos;s Orders by Product</CardTitle>
            <CardDescription>Products ordered for delivery today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {getTodayOrders().map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 rounded-md border bg-neutral-50/50">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground truncate">{item.name}</p>
                    <p className="font-display text-3xl font-extrabold text-brand-navy mt-1">{item.quantity}</p>
                  </div>
                  <Package className="h-8 w-8 text-muted-foreground shrink-0 ml-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {showTodayOrders && getTodayOrders().length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            No orders scheduled for delivery today
          </CardContent>
        </Card>
      )}

      {/* Stats Cards - Only show when not in Today's Orders view */}
      {!showTodayOrders && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigateToOrders && onNavigateToOrders(null)}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">Total Orders</p>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="font-display text-4xl font-extrabold mt-2 text-brand-navy">{stats.total}</p>
            <p className="text-xs text-muted-foreground mt-1">In selected period</p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigateToOrders && onNavigateToOrders("pending")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">Pending</p>
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="font-display text-4xl font-extrabold mt-2 text-yellow-700">{stats.pending}</p>
            <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigateToOrders && onNavigateToOrders("dispatched")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">Dispatched</p>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="font-display text-4xl font-extrabold mt-2 text-amber-700">{stats.dispatched}</p>
            <p className="text-xs text-muted-foreground mt-1">In delivery</p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigateToOrders && onNavigateToOrders("invoiced")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">Invoiced</p>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="font-display text-4xl font-extrabold mt-2 text-green-700">{stats.invoiced}</p>
            <p className="text-xs text-muted-foreground mt-1">Completed</p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Charts - Only show when not in Today's Orders view */}
      {!showTodayOrders && (
      <div className="grid grid-cols-1 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl text-brand-navy">Revenue Trend</CardTitle>
            <CardDescription className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
              Total revenue by date
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenueTrendData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0284C7" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#0284C7" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  axisLine={{ stroke: "#E2E8F0" }}
                  tickLine={false}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,59,92,0.12)", fontSize: 12 }}
                  labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  formatter={(value) => `$${value.toFixed(2)}`}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0284C7"
                  strokeWidth={2.5}
                  fill="url(#revGrad)"
                  name="Revenue"
                  activeDot={{ r: 6, strokeWidth: 2, fill: "#0284C7", stroke: "#fff" }}
                  dot={{ r: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Product Quantity Trend */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="font-display text-xl text-brand-navy">Product Quantity Trend</CardTitle>
                <CardDescription className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground mt-1.5">
                  Products ordered in this period · hover a legend item to focus
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="product-trend-filter-btn" className="shrink-0 gap-1.5">
                    <ListFilter className="h-4 w-4" />
                    {productFilter ? `${visibleProducts.length}/${productTrend.productNames.length} products` : "All products"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="end" data-testid="product-trend-filter-popover">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Show products</span>
                    <div className="flex items-center gap-2">
                      <button
                        className="text-[11px] text-brand-navy underline"
                        data-testid="product-trend-filter-all"
                        onClick={() => setProductFilter(null)}
                      >
                        Select all
                      </button>
                      <button
                        className="text-[11px] text-rose-700 underline"
                        data-testid="product-trend-filter-none"
                        onClick={() => setProductFilter(new Set())}
                      >
                        Deselect all
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                    {productTrend.productNames.map((name) => (
                      <label key={name} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                        <Checkbox
                          checked={!productFilter || productFilter.has(name)}
                          onCheckedChange={() => toggleProduct(name)}
                          data-testid={`product-trend-filter-${name}`}
                        />
                        <span
                          className="w-2.5 h-2.5 rounded-sm shrink-0"
                          style={{ background: isLightColor(getProductColor(name)) ? "#0F4C75" : getProductColor(name) }}
                        />
                        <span className="truncate">{truncateName(name, 26)}</span>
                      </label>
                    ))}
                    {productTrend.productNames.length === 0 && (
                      <div className="text-xs text-muted-foreground py-2">No products in this range</div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={productTrend.data} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  axisLine={{ stroke: "#E2E8F0" }}
                  tickLine={false}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,59,92,0.12)", fontSize: 12 }}
                  labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                />
                <Legend
                  onMouseEnter={(e) => setHoveredLine(e.dataKey)}
                  onMouseLeave={() => setHoveredLine(null)}
                  wrapperStyle={{ cursor: 'pointer', fontSize: 12 }}
                  iconType="plainline"
                />
                {visibleProducts.map((name) => {
                  // White / very light product colors are invisible on a white
                  // chart background — fall back to brand navy.
                  const c = getProductColor(name);
                  const stroke = isLightColor(c) ? "#0F4C75" : c;
                  return (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={stroke}
                      strokeWidth={hoveredLine === name ? 4 : 2.25}
                      strokeOpacity={hoveredLine && hoveredLine !== name ? 0.25 : 1}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      dot={{ r: 0 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Pie Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Revenue Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-xl text-brand-navy">Revenue by Product</CardTitle>
              <CardDescription className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                Top products by revenue
              </CardDescription>
            </CardHeader>
            <CardContent>
              {getProductRevenue().length > 0 ? (
                <ProductPie
                  data={getProductRevenue()}
                  formatValue={(v) => `$${v.toFixed(2)}`}
                  colorFn={getProductColor}
                />
              ) : (
                <div className="h-[350px] flex items-center justify-center text-muted-foreground text-sm">
                  No revenue data in current time range
                </div>
              )}
            </CardContent>
          </Card>

          {/* Product Quantity Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display text-xl text-brand-navy">Quantity by Product</CardTitle>
              <CardDescription className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                Top products by quantity
              </CardDescription>
            </CardHeader>
            <CardContent>
              {getProductQuantity().length > 0 ? (
                <ProductPie
                  data={getProductQuantity()}
                  formatValue={(v) => `${v} items`}
                  colorFn={getProductColor}
                />
              ) : (
                <div className="h-[350px] flex items-center justify-center text-muted-foreground text-sm">
                  No quantity data in current time range
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}

// Pie chart with proper handling of:
//   - white / very-light product colors (rendered with dark navy stroke + a
//     subtle off-white fill so the slice stays visible)
//   - long product names (kept in a side legend list instead of external pie
//     labels that get cut off at the card edge)
function ProductPie({ data, formatValue, colorFn }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-center">
      <div className="sm:col-span-3">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ percent }) => (percent >= 0.05 ? `${(percent * 100).toFixed(0)}%` : "")}
              outerRadius={100}
              innerRadius={55}
              paddingAngle={1}
              dataKey="value"
            >
              {data.map((entry, index) => {
                const c = colorFn(entry.name) || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
                const light = isLightColor(c);
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={light ? "#F1F5F9" : c}
                    stroke={light ? "#0F4C75" : "#ffffff"}
                    strokeWidth={light ? 1.5 : 2}
                  />
                );
              })}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 12 }}
              formatter={(value, name) => [formatValue(value), name]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="sm:col-span-2 space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
        {data.map((entry, index) => {
          const c = colorFn(entry.name) || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
          const light = isLightColor(c);
          const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : "0.0";
          return (
            <div
              key={entry.name}
              className="flex items-center justify-between gap-2 text-xs border-b last:border-0 pb-1.5"
              title={entry.name}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{
                    background: light ? "#F1F5F9" : c,
                    border: light ? "1px solid #0F4C75" : "none",
                  }}
                />
                <span className="truncate font-medium">{truncateName(entry.name, 28)}</span>
              </span>
              <span className="font-mono text-[11px] text-muted-foreground shrink-0">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

